'use strict';

/**
 * Markup-Templates
 * ----------------
 * The shared rendering half of Pict-Provider-Markup: one title block, one cell strip, one tooltip,
 * spread into any view that shows an entity.
 *
 * What is shared here is the MARK, not the container. A card on the kanban is draggable and opens a
 * record; a row in a list is not; a tooltip floats. Those wrappers belong to their surfaces and carry
 * their own handlers. The designator cell, the ruled title block, and the labelled cells underneath
 * are identical everywhere, so they live here and every surface composes them:
 *
 *   {~TS:Markup-TitleBlock:Record.Mark~}    the mark: designator cell + title, ruled
 *   {~TS:Markup-Designator:Record.Mark~}    the designator alone, for a surface with its own columns
 *   {~TS:Markup-Cell:Record.Cells~}         the labelled cells, one per entry
 *   {~TS:Markup-Tooltip:Record.Tip~}        the loaded tooltip body
 *
 * Templates are view-owned in Pict, so these are exported as plain definitions to spread into a
 * view's own Templates array rather than registered globally. That keeps one source of truth without
 * fighting the framework's model.
 *
 * Visual direction is the drafting title block: square corners, hairline rules, the designator in its
 * own bordered cell. Colour comes entirely from theme tokens, so the mark holds on Plan Sheet paper
 * and on a Blueprint cyanotype without a second definition.
 */

// The mark, and the furniture around it. No backticks anywhere in this string: it is interpolated
// into a CSS template literal by whichever view spreads it.
const MarkupCSS = /*css*/`
	.ps-mark { display: flex; align-items: stretch; border: 1px solid var(--theme-color-border-strong, #a89f87);
		background: var(--theme-color-background-panel, #fbf8f0); min-width: 0; }
	.ps-mark-d { flex: 0 0 auto; display: flex; align-items: center; padding: 4px 8px; font-size: 11.5px;
		font-weight: 700; letter-spacing: 0.05em; font-variant-numeric: tabular-nums;
		font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
		border-right: 1px solid var(--theme-color-border-strong, #a89f87);
		background: var(--theme-color-background-tertiary, #e5dcc7);
		color: var(--theme-color-text-primary, #2b2d2f); white-space: nowrap; }
	.ps-mark-t { flex: 1; min-width: 0; padding: 4px 9px; font-size: 13px; line-height: 1.35;
		color: var(--theme-color-text-primary, #2b2d2f); }
	.ps-mark-inline { display: inline-flex; vertical-align: middle; font-size: 12px; }
	.ps-mark-inline .ps-mark-t { padding: 2px 7px; font-size: 12px; }
	.ps-mark-inline .ps-mark-d { padding: 2px 6px; font-size: 11px; }

	/* The designator on its own, for a surface that already has columns of its own to sit in (a
	   list row). Only the typography of the mark lives here: WI-014 has to read as the same object
	   it does on a card. Whether it gets a box, a rule, or a background is the container's call,
	   so a list cell adds that itself rather than this reaching into the surface. */
	.ps-mark-solo { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
		font-variant-numeric: tabular-nums; font-weight: 700; font-size: 11.5px; letter-spacing: 0.05em;
		white-space: nowrap; color: var(--theme-color-brand-primary, #1d5fa8); }

	.ps-cells { display: flex; flex-wrap: wrap; gap: 0; border-top: 1px solid var(--theme-color-border-light, #dfd7c4); }
	.ps-cell { flex: 1 1 0; min-width: 0; padding: 3px 8px;
		border-right: 1px solid var(--theme-color-border-light, #dfd7c4); }
	.ps-cell:last-child { border-right: 0; }
	.ps-cell-k { display: block; font-size: 8.5px; letter-spacing: 0.13em; text-transform: uppercase;
		color: var(--theme-color-text-muted, #6c6a60); }
	.ps-cell-v { display: block; font-size: 11.5px; color: var(--theme-color-text-secondary, #46484a);
		overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

	.ps-tip { min-width: 260px; max-width: 400px; }
	.ps-tip-desc { margin: 9px 0 0; font-size: 12.5px; line-height: 1.45;
		color: var(--theme-color-text-secondary, #46484a); }
	.ps-tip-refs { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 10px; }
	.ps-xref { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 10.5px;
		font-weight: 700; font-variant-numeric: tabular-nums; padding: 1px 6px;
		border: 1px solid var(--theme-color-brand-primary, #1d5fa8);
		color: var(--theme-color-brand-primary, #1d5fa8); }
`;

// The mark. Rendered over a Mark record from the provider, so a designator-less entity still renders
// its title cleanly rather than leaving an empty box.
const MarkupTemplates =
[
	{
		Hash: 'Markup-TitleBlock',
		Template: /*html*/`<div class="ps-mark {~D:Record.KindClass~}"><span class="ps-mark-d">{~D:Record.Designator~}</span><span class="ps-mark-t">{~D:Record.Title~}</span></div>`
	},
	{
		Hash: 'Markup-TitleBlock-Inline',
		Template: /*html*/`<span class="ps-mark ps-mark-inline {~D:Record.KindClass~}"><span class="ps-mark-d">{~D:Record.Designator~}</span><span class="ps-mark-t">{~D:Record.Title~}</span></span>`
	},
	{
		// The mark with nothing around it. A list row already rules its own columns, so it takes the
		// designator alone rather than the title block, and still cannot drift from what a card shows.
		Hash: 'Markup-Designator',
		Template: /*html*/`<span class="ps-mark-solo {~D:Record.KindClass~}">{~D:Record.Designator~}</span>`
	},
	{
		Hash: 'Markup-Cell',
		// data-k names the cell (Type, Pri, By, For, ...) so a surface that only wants some of them --
		// the board hiding By/For until "expanded" is toggled on -- can target one by CSS without a
		// second cell template.
		Template: /*html*/`<div class="ps-cell" data-k="{~D:Record.K~}"><span class="ps-cell-k">{~D:Record.K~}</span><span class="ps-cell-v">{~D:Record.V~}</span></div>`
	},
	{
		Hash: 'Markup-Tooltip',
		Template: /*html*/`
<div class="ps-tip">
	<div class="ps-mark {~D:Record.KindClass~}"><span class="ps-mark-d">{~D:Record.Designator~}</span><span class="ps-mark-t">{~D:Record.Title~}</span></div>
	<div class="ps-cells">{~TS:Markup-Cell:Record.Cells~}</div>
	<p class="ps-tip-desc">{~D:Record.Description~}</p>
	<div class="ps-tip-refs">{~TS:Markup-Xref:Record.References~}</div>
</div>`
	},
	{
		Hash: 'Markup-Xref',
		Template: /*html*/`<span class="ps-xref">{~D:Record~}</span>`
	}
];

module.exports = { MarkupTemplates: MarkupTemplates, MarkupCSS: MarkupCSS };
